/** Upscale source canvas 2× (minimum width 960px for mobile OCR). */
function upscaleCanvas(sourceCanvas, scale = 2) {
  const w = Math.max(960, Math.floor(sourceCanvas.width * scale))
  const h = Math.max(120, Math.round(sourceCanvas.height * (w / sourceCanvas.width)))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(sourceCanvas, 0, 0, w, h)
  return canvas
}

function readGray(imageData) {
  const { data, width, height } = imageData
  const gray = new Float32Array(width * height)
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
  }
  return { gray, width, height }
}

/** Stretch histogram for stronger character separation. */
function boostContrast(gray, width, height) {
  let min = 255
  let max = 0
  for (let i = 0; i < gray.length; i += 1) {
    min = Math.min(min, gray[i])
    max = Math.max(max, gray[i])
  }
  const range = Math.max(max - min, 1)
  for (let i = 0; i < gray.length; i += 1) {
    gray[i] = ((gray[i] - min) / range) * 255
  }
  return { gray, width, height }
}

/** 3×3 sharpen kernel. */
function sharpenGray(gray, width, height) {
  const out = new Float32Array(gray.length)
  const kernel = [0, -1, 0, -1, 5, -1, 0, -1, 0]
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let sum = 0
      for (let ky = -1; ky <= 1; ky += 1) {
        for (let kx = -1; kx <= 1; kx += 1) {
          const px = Math.min(width - 1, Math.max(0, x + kx))
          const py = Math.min(height - 1, Math.max(0, y + ky))
          const ki = (ky + 1) * 3 + (kx + 1)
          sum += gray[py * width + px] * kernel[ki]
        }
      }
      out[y * width + x] = Math.min(255, Math.max(0, sum))
    }
  }
  return { gray: out, width, height }
}

/** Adaptive mean threshold (local neighbourhood). */
function adaptiveMeanThreshold(gray, width, height, blockSize = 15, c = 8) {
  const out = new Uint8ClampedArray(gray.length)
  const half = Math.floor(blockSize / 2)
  const integral = new Float64Array((width + 1) * (height + 1))

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = y * width + x
      const top = (y + 1) * (width + 1) + (x + 1)
      integral[top] =
        gray[idx] +
        integral[y * (width + 1) + (x + 1)] +
        integral[(y + 1) * (width + 1) + x] -
        integral[y * (width + 1) + x]
    }
  }

  const areaSum = (x0, y0, x1, y1) => {
    const a = (y0) * (width + 1) + (x0)
    const b = (y0) * (width + 1) + (x1 + 1)
    const d = (y1 + 1) * (width + 1) + (x0)
    const e = (y1 + 1) * (width + 1) + (x1 + 1)
    return integral[e] - integral[b] - integral[d] + integral[a]
  }

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const x0 = Math.max(0, x - half)
      const y0 = Math.max(0, y - half)
      const x1 = Math.min(width - 1, x + half)
      const y1 = Math.min(height - 1, y + half)
      const count = (x1 - x0 + 1) * (y1 - y0 + 1)
      const mean = areaSum(x0, y0, x1, y1) / count
      out[y * width + x] = gray[y * width + x] > mean - c ? 255 : 0
    }
  }

  return { gray: out, width, height }
}

function grayToCanvas(grayLike, width, height) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  const imageData = ctx.createImageData(width, height)
  for (let p = 0; p < grayLike.length; p += 1) {
    const v = Math.round(grayLike[p] ?? 0)
    const i = p * 4
    imageData.data[i] = v
    imageData.data[i + 1] = v
    imageData.data[i + 2] = v
    imageData.data[i + 3] = 255
  }
  ctx.putImageData(imageData, 0, 0)
  return canvas
}

function invertGray(gray) {
  const out = new Float32Array(gray.length)
  for (let i = 0; i < gray.length; i += 1) out[i] = 255 - gray[i]
  return out
}

/**
 * Preprocess a captured plate crop before OCR.
 * @param {HTMLCanvasElement} sourceCanvas
 * @param {{ threshold?: boolean, invert?: boolean, raw?: boolean }} options
 */
export function preprocessPlateCapture(sourceCanvas, { threshold = true, invert = false, raw = false } = {}) {
  if (raw) return upscaleCanvas(sourceCanvas)

  const upscaled = upscaleCanvas(sourceCanvas)
  const ctx = upscaled.getContext('2d', { willReadFrequently: true })
  let { gray, width, height } = readGray(ctx.getImageData(0, 0, upscaled.width, upscaled.height))
  ;({ gray, width, height } = boostContrast(gray, width, height))
  ;({ gray, width, height } = sharpenGray(gray, width, height))

  if (invert) gray = invertGray(gray)

  if (threshold) {
    const bin = adaptiveMeanThreshold(gray, width, height)
    return grayToCanvas(bin.gray, width, height)
  }

  return grayToCanvas(gray, width, height)
}

/** Several preprocess variants — OCR engines perform differently on each. */
export function preprocessPlateVariants(sourceCanvas) {
  return [
    { id: 'raw', canvas: preprocessPlateCapture(sourceCanvas, { raw: true }) },
    { id: 'gray', canvas: preprocessPlateCapture(sourceCanvas, { threshold: false }) },
    { id: 'threshold', canvas: preprocessPlateCapture(sourceCanvas, { threshold: true }) },
    { id: 'inverted', canvas: preprocessPlateCapture(sourceCanvas, { threshold: true, invert: true }) },
  ]
}
